import { CheckedBaggageIcon, type SSRModalCardSelectionProps } from '@fz/ui-components-react';
import type { BaggageData } from '@models/landing/api/baggage.model';
import { PassengerTypeCode, SsrType } from '@models/common/shared-enums.model';
import { getResource } from '@utils/resource-translator.util';
import {
  formatPassengerDisplayName,
  isInfantPassengerTypeLabel,
  resolvePassengerTypeLabel,
  resolveDefaultSelectablePassengerId,
  sortPassengersWithAssignedInfants,
} from '@shared/components/ssr-passenger.util';

const CHECKED_BAGGAGE_ICON = <CheckedBaggageIcon size={24} />;
const DEFAULT_HAND_BAGGAGE = '7 kg';
const DEFAULT_CHECKED_BAGGAGE = getResource('Extras.NoCheckedIn.Baggage.Text');
const BAGGAGE_TITLE_RESOURCE_KEY = 'SelectPassenger.Baggage.Text';
const DEFAULT_CONFIRM_LABEL = 'Extras.Confirm.Button.Text';
const DEFAULT_CONTENT_TITLE = 'Extras.Baggage.Header.Text';
const DEFAULT_INFO_BANNER_TEXT = 'Extras.Baggage.VAT.Text';
const MOST_POPULAR_BADGE_TEXT_KEY = 'ContactDetails.Popular.Text';
const CHECKED_BAGGAGE_SSR_TYPE = String(SsrType.CHECKEDBAGGAGE);
const INFANT_PASSENGER_TYPE = Number(PassengerTypeCode.Infant);

export type BaggageModalPassenger = SSRModalCardSelectionProps['passengers'][number];

export type BaggageModalTabItem = NonNullable<
  SSRModalCardSelectionProps['flightTabItems']
>[number] & {
  logicalFlightID?: string;
  isInterline?: boolean;
};

export type BaggageModalOption = SSRModalCardSelectionProps['options'][number] & {
  logicalFlightId: string;
  info: BaggageModalOptionInfo[];
};

export interface BaggageModalOptionInfoApplicablePax {
  paxJourneyId: string;
  resPaxId: number;
  recordNumber: number;
}

export interface BaggageModalOptionInfo {
  id: string;
  label: string;
  price: number;
  currency: string;
  col?: string;
  amount: string;
  quantityAvailable: number;
  chargeComment: string;
  codeType: string;
  isBusiness: boolean;
  secureHash: string;
  isPurchased: boolean;
  applicablePax: BaggageModalOptionInfoApplicablePax[];
  mostPopularBadgeText?: string;
}

export interface BaggageModalViewModel {
  title: SSRModalCardSelectionProps['title'];
  confirmButtonLabel: SSRModalCardSelectionProps['confirmButtonLabel'];
  contentTitle: SSRModalCardSelectionProps['contentTitle'];
  isUnavailable: SSRModalCardSelectionProps['isUnavailable'];
  unavailableTitle: SSRModalCardSelectionProps['unavailableTitle'];
  unavailableDescription: SSRModalCardSelectionProps['unavailableDescription'];
  unavailableVariant: SSRModalCardSelectionProps['unavailableVariant'];
  currencyCode: SSRModalCardSelectionProps['currencyCode'];
  infoBannerText: SSRModalCardSelectionProps['infoBannerText'];
  includedBaggageInfo: {
    primaryValue: string;
    secondaryValue: string;
  };
  passengers: SSRModalCardSelectionProps['passengers'];
  passengerSSRDetails: Record<string, { subText1: string; subText2: string }>;
  flightTabItems: BaggageModalTabItem[];
  options: SSRModalCardSelectionProps['options'] & BaggageModalOption[];
  defaultSelectedPassengerId: string;
  defaultSelectedOptionId: string;
  defaultActiveFlightTabValue: string;
}

export interface BaggageAddApiPayload {
  [key: string]: unknown;
  ssrType: string;
  logicalFlightId: string;
  physicalFlightId: string;
  amount: string;
  paxJourneyId: string;
  resPaxId: number;
  recordNumber: number;
  quantityAvailable: number;
  chargeComment: string;
  codeType: string;
  isBusiness: boolean;
  secureHash: string;
}

const mapPassengers = (baggage?: BaggageData): BaggageModalPassenger[] =>
  sortPassengersWithAssignedInfants(baggage?.passengers ?? []).map((passenger) => ({
    id: passenger.resPaxId.toString(),
    name: formatPassengerDisplayName(passenger.title, passenger.firstName, passenger.lastName),
    type: resolvePassengerTypeLabel(passenger.passengerType),
    isInfant: Number(passenger.passengerType) === INFANT_PASSENGER_TYPE,
    // seat and pnr come from retrieve metadata stitched into BaggageData by extras.mapper
    // keyed by passenger resPaxId for quick lookup.
    seatNumber: baggage?.passengerDisplayInfoByResPaxId?.[passenger.resPaxId]?.seatNumber ?? '',
    pnrNumber: baggage?.passengerDisplayInfoByResPaxId?.[passenger.resPaxId]?.pnrNumber,
  }));

const resolveInfoPurchased = (
  baggage: BaggageData | undefined,
  logicalFlightId: string,
  codeType: string,
  applicablePaxIds: number[],
): boolean => {
  if (!baggage || applicablePaxIds.length === 0) {
    return false;
  }

  return baggage.passengers.some(
    (passenger) =>
      applicablePaxIds.includes(passenger.resPaxId) &&
      passenger.flightsDetail.some(
        (flightDetail) =>
          flightDetail.logicalFlightId === logicalFlightId &&
          flightDetail.includedExtrasList.some(
            (extra) =>
              extra.ssrType.toUpperCase() === CHECKED_BAGGAGE_SSR_TYPE &&
              extra.codeType === codeType,
          ),
      ),
  );
};

const mapInfoItem = (
  infoItem: BaggageData['options'][number]['info'][number],
  option: BaggageData['options'][number],
  index: number,
  baggage: BaggageData | undefined,
  preferredServices?: readonly string[],
): BaggageModalOptionInfo => {
  const applicablePax = infoItem.applicablePax.map((applicablePaxItem) => ({
    paxJourneyId: applicablePaxItem.paxJourneyId,
    resPaxId: applicablePaxItem.resPaxId,
    recordNumber: applicablePaxItem.recordNumber,
  }));

  const isPreferredService = preferredServices?.some(
    (serviceCode) => serviceCode === infoItem.codeType,
  );

  const mostPopularBadgeText = isPreferredService
    ? getResource(MOST_POPULAR_BADGE_TEXT_KEY)
    : undefined;

  return {
    id: `${option.id}-${infoItem.codeType}-${index}`,
    label: infoItem.label,
    price: infoItem.price,
    currency: option.currency,
    col: option.col,
    amount: infoItem.amount,
    quantityAvailable: infoItem.quantityAvailable,
    chargeComment: infoItem.chargeComment,
    codeType: infoItem.codeType,
    isBusiness: infoItem.isBusiness,
    secureHash: infoItem.secureHash,
    isPurchased:
      infoItem.isPurchased ??
      resolveInfoPurchased(
        baggage,
        option.logicalFlightId,
        infoItem.codeType,
        applicablePax.map((item) => item.resPaxId),
      ),
    applicablePax,
    mostPopularBadgeText,
  };
};

const mapOptions = (
  baggage?: BaggageData,
  preferredServices?: readonly string[],
): BaggageModalOption[] => {
  return (baggage?.options ?? []).flatMap((option) => {
    const mappedInfo = option.info
      .filter((infoItem) => infoItem.applicablePax.length > 0)
      .map((infoItem, index) => mapInfoItem(infoItem, option, index, baggage, preferredServices));

    if (mappedInfo.length === 0) {
      return [];
    }

    return [
      {
        id: option.id,
        label: mappedInfo[0].label,
        price: mappedInfo[0].price,
        currency: option.currency,
        logicalFlightId: option.logicalFlightId,
        icon: CHECKED_BAGGAGE_ICON,
        col: option.col,
        isPurchased: mappedInfo.some((infoItem) => infoItem.isPurchased),
        info: mappedInfo,
      },
    ];
  });
};

const mapPassengerSSRDetails = (
  passengers: BaggageModalPassenger[],
  primaryBaggage: string,
  baggage?: BaggageData,
  activeFlightTabValue?: string,
): Record<string, { subText1: string; subText2: string; infantSubText?: string }> =>
  passengers.reduce<Record<string, { subText1: string; subText2: string; infantSubText?: string }>>(
    (acc, passenger) => {
      const tabIndex = activeFlightTabValue?.toLowerCase().includes('return') ? 1 : 0;
      const includedInfoByResPaxIdForTab =
        baggage?.includedBaggageInfoByResPaxIdByFlight?.[tabIndex] ??
        baggage?.includedBaggageInfoByResPaxIdByFlight?.[0] ??
        baggage?.includedBaggageInfoByResPaxId;
      const passengerResPaxId = Number(passenger.id);
      const passengerIncludedBaggageInfo = includedInfoByResPaxIdForTab?.find(
        (item) => item.resPaxId === passengerResPaxId,
      );
      const mappedPrimaryBaggage = passengerIncludedBaggageInfo?.primaryValue ?? primaryBaggage;
      const checkedBaggageActionLabel = hasPurchasedCheckedBaggage(
        baggage,
        passenger.id,
        activeFlightTabValue,
      )
        ? 'Extras.ViewBaggage.Text'
        : 'Extras.AddBaggage.Text';

      acc[passenger.id] = {
        subText1: `${mappedPrimaryBaggage} ${getResource('Extras.HandBaggage.Text')}`,
        subText2: checkedBaggageActionLabel,
        infantSubText: isInfantPassengerTypeLabel(passenger.type)
          ? getResource('SelectPassenger.NotApplCheckedInBaggage.Text')
          : undefined,
      };
      return acc;
    },
    {},
  );

const mapFlightTabs = (baggage?: BaggageData): BaggageModalTabItem[] =>
  (baggage?.flightTabItems ?? []).map((flightTabItem) => ({
    value: flightTabItem.value,
    label: flightTabItem.label,
    content: flightTabItem.content,
    logicalFlightID: flightTabItem.logicalFlightID,
    isInterline: flightTabItem.isInterline,
  }));

const getIncludedBaggageInfo = (
  baggage?: BaggageData,
): { primaryValue: string; secondaryValue: string } =>
  baggage?.includedBaggageInfo ?? {
    primaryValue: DEFAULT_HAND_BAGGAGE,
    secondaryValue: DEFAULT_CHECKED_BAGGAGE,
  };

// eslint-disable-next-line complexity
const getModalTextFields = (
  baggage?: BaggageData,
): {
  title: string;
  confirmButtonLabel: string;
  contentTitle: string;
  currencyCode: string;
  infoBannerText: string;
} => {
  const localizedTitle = getResource(BAGGAGE_TITLE_RESOURCE_KEY);
  const resolvedTitle =
    localizedTitle && localizedTitle !== BAGGAGE_TITLE_RESOURCE_KEY
      ? localizedTitle
      : BAGGAGE_TITLE_RESOURCE_KEY;

  const localizedConfirmLabel = getResource(DEFAULT_CONFIRM_LABEL);
  const resolvedConfirmLabel =
    localizedConfirmLabel && localizedConfirmLabel !== DEFAULT_CONFIRM_LABEL
      ? localizedConfirmLabel
      : DEFAULT_CONFIRM_LABEL;

  const localizedContentTitle = getResource(DEFAULT_CONTENT_TITLE);
  const resolvedContentTitle =
    localizedContentTitle && localizedContentTitle !== DEFAULT_CONTENT_TITLE
      ? localizedContentTitle
      : DEFAULT_CONTENT_TITLE;

  const localizedInfoBannerText = getResource(DEFAULT_INFO_BANNER_TEXT);
  const resolvedInfoBannerText =
    localizedInfoBannerText && localizedInfoBannerText !== DEFAULT_INFO_BANNER_TEXT
      ? localizedInfoBannerText
      : DEFAULT_INFO_BANNER_TEXT;

  return {
    title: baggage?.title ?? resolvedTitle,
    confirmButtonLabel: baggage?.confirmButtonLabel ?? resolvedConfirmLabel,
    contentTitle: baggage?.contentTitle ?? resolvedContentTitle,
    currencyCode: baggage?.currencyCode ?? 'AED',
    infoBannerText: baggage?.infoBannerText ?? resolvedInfoBannerText,
  };
};

const hasPurchasedCheckedBaggage = (
  baggage: BaggageData | undefined,
  passengerId: string,
  activeFlightTabValue?: string,
): boolean => {
  if (!baggage) {
    return false;
  }

  const passenger = baggage.passengers.find((item) => item.resPaxId.toString() === passengerId);
  if (!passenger) {
    return false;
  }

  const activeLogicalFlightId = baggage.flightTabItems.find(
    (tab) => tab.value === activeFlightTabValue,
  )?.logicalFlightID;

  return passenger.flightsDetail.some((flightDetail) => {
    const matchesActiveFlight =
      !activeFlightTabValue ||
      flightDetail.logicalFlightId === activeFlightTabValue ||
      flightDetail.logicalFlightId === activeLogicalFlightId;

    return (
      matchesActiveFlight &&
      flightDetail.includedExtrasList.some(
        (extra) => extra.ssrType.toUpperCase() === CHECKED_BAGGAGE_SSR_TYPE && extra.isPurchased,
      )
    );
  });
};

export const mapBaggageToModalView = (
  baggage?: BaggageData,
  physicalFlightId?: string,
  preferredServices?: readonly string[],
): BaggageModalViewModel => {
  const modalTextFields = getModalTextFields(baggage);
  const includedBaggageInfo = getIncludedBaggageInfo(baggage);
  const mappedPassengers = mapPassengers(baggage);
  const mappedOptions = mapOptions(baggage, preferredServices);
  const mappedFlightTabs = mapFlightTabs(baggage);

  // Find the tab matching the physicalFlightId, or default to first tab
  const defaultActiveFlightTabValue = physicalFlightId
    ? (mappedFlightTabs.find((tab) => tab.value === physicalFlightId)?.value ??
      mappedFlightTabs[0]?.value ??
      '')
    : (mappedFlightTabs[0]?.value ?? '');

  const mappedPassengerSSRDetails = mapPassengerSSRDetails(
    mappedPassengers,
    includedBaggageInfo.primaryValue,
    baggage,
    defaultActiveFlightTabValue,
  );
  return {
    title: modalTextFields.title,
    confirmButtonLabel: modalTextFields.confirmButtonLabel,
    contentTitle: modalTextFields.contentTitle,
    isUnavailable: false,
    unavailableTitle: undefined,
    unavailableDescription: undefined,
    unavailableVariant: undefined,
    currencyCode: modalTextFields.currencyCode,
    infoBannerText: modalTextFields.infoBannerText,
    includedBaggageInfo,
    passengers: mappedPassengers,
    passengerSSRDetails: mappedPassengerSSRDetails,
    flightTabItems: mappedFlightTabs,
    options: mappedOptions,
    defaultSelectedPassengerId: resolveDefaultSelectablePassengerId(mappedPassengers),
    defaultSelectedOptionId: mappedOptions[0]?.info[0]?.id ?? '',
    defaultActiveFlightTabValue,
  };
};

const resolveSelectedPassenger = (
  baggage: BaggageData,
  selectedPassengerId: string,
): BaggageData['passengers'][number] | undefined => {
  const parsedPassengerId = Number(selectedPassengerId);
  return baggage.passengers.find((passenger) => passenger.resPaxId === parsedPassengerId);
};

const resolvePhysicalFlightId = (
  passenger: BaggageData['passengers'][number],
  logicalFlightId: string,
): string => {
  return (
    passenger.flightsDetail.find((flightDetail) => flightDetail.logicalFlightId === logicalFlightId)
      ?.physicalFlightId ?? ''
  );
};

export const mapBaggageSelectionToAddPayload = (
  baggage: BaggageData,
  selectedPassengerId: string,
  selectedOptionId: string,
): BaggageAddApiPayload | null => {
  const selectedPassenger = resolveSelectedPassenger(baggage, selectedPassengerId);
  const selectedOption = baggage.options.find(
    (option) =>
      option.id === selectedOptionId || option.info.some((info) => info.id === selectedOptionId),
  );

  if (!selectedPassenger || !selectedOption) {
    return null;
  }

  const selectedInfoById = selectedOption.info.find((info) => info.id === selectedOptionId);
  const selectedInfo =
    selectedInfoById ??
    selectedOption.info.find((info) =>
      info.applicablePax.some(
        (applicablePax) => applicablePax.resPaxId === selectedPassenger.resPaxId,
      ),
    ) ??
    selectedOption.info[0];

  const selectedApplicablePax = selectedInfo?.applicablePax.find(
    (applicablePax) => applicablePax.resPaxId === selectedPassenger.resPaxId,
  );

  if (!selectedInfo || !selectedApplicablePax) {
    return null;
  }

  return {
    ssrType: selectedOption.ssrType,
    logicalFlightId: selectedOption.logicalFlightId,
    physicalFlightId: resolvePhysicalFlightId(selectedPassenger, selectedOption.logicalFlightId),
    amount: selectedInfo.amount,
    paxJourneyId: selectedApplicablePax.paxJourneyId,
    resPaxId: selectedApplicablePax.resPaxId,
    recordNumber: selectedApplicablePax.recordNumber,
    quantityAvailable: selectedInfo.quantityAvailable,
    chargeComment: selectedInfo.chargeComment,
    codeType: selectedInfo.codeType,
    isBusiness: selectedInfo.isBusiness,
    secureHash: selectedInfo.secureHash,
  };
};
